import { Group, SaveButton } from '@/components/_common';
import { InvoiceOriginReferenceEntity } from '@/core/domain/invoice/invoices/invoices.enums';
import { Badge, Button, Form, Modal, ModalProps, message } from 'antd';
import StatusTag from '@/components/_common/StatusTag';
import { InvoiceCreateDTO } from '@/core/domain/invoice/invoice/Invoice.types';
import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import TabsCustom from '@/components/_common/TabsCustom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useCreateInvoiceNoteItems,
  useUpdateInvoiceNoteItemsId,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItms.hooks';
import {
  InvoiceItemCreateRequest,
  InvoiceItemTaxes,
  TypeEnum,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { useCalcTaxesItems } from '@/core/domain/invoice/invoiceItem/invoiceItem.hook';
import InvoicePartCofinsForm from './CofinsForm';
import InvoicePartFuelForm from './FuelForm';
import InvoicePartGeneralDataForm from './GeneralDataForm';
import InvoicePartIcmsForm from './IcmsForm';
import {
  useInvoicePartCreateFillFields,
  useInvoicePartDetailFillFieds,
} from './InvoicePart.hooks';
import InvoicePartIpiForm from './IpiForm';
import InvoicePartPisForm from './PisForm';
import InvoicePartShareForm from './ShareForm';
import { InvoicePartFormValues } from './InvoicePartForm.types';
import { removeMask } from '@/utils/mask';
import InvoicePartIrpjCsllForm from './IrpjCsllForm';
import { useInvoiceFormContext } from '../../InvoiceFormProvider';
import IbsCbsFormFields from '@/components/_common/TaxReform/IbsCbsFormFields';
// IS (Imposto Seletivo, UB01-UB11) — fora da NT v1.40; reativar quando
// entrar em vigor.
// import IsFormFields from '@/components/_common/TaxReform/IsFormFields';

interface Props extends ModalProps {
  part?: StockItem;
  invoice?: InvoiceCreateDTO; // TODO: Corrigir a tipagem
  onCancel: VoidFunction;
  invoiceItemId?: number;
}

const InvoicePartModal: React.FC<Props> = (props) => {
  const { part, invoice, invoiceItemId, ...others } = props;

  const { disabledFields } = useInvoiceFormContext();

  const [form] = Form.useForm<InvoicePartFormValues>();

  const [menuKey, setMenuKey] = useState('general');
  const [tabErrors, setTabErrors] = useState<string[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  const invoiceItemCreate = useCreateInvoiceNoteItems();
  const invoiceItemEdit = useUpdateInvoiceNoteItemsId();
  const invoiceCalculateTaxesItem = useCalcTaxesItems();

  const [createFillFields, createFieldsLoading] =
    useInvoicePartCreateFillFields({
      companyId: invoice?.companyId,
      invoiceType: invoice?.invoiceType,
    });

  const [detailFillFields, detailFieldsLoading] =
    useInvoicePartDetailFillFieds();

  const hasLoading =
    submitLoading ||
    createFieldsLoading ||
    detailFieldsLoading ||
    invoiceCalculateTaxesItem.isLoading;

  useEffect(() => {
    // Automatic completion of fields when creating note item
    (async () => {
      if (invoice?.id && invoice.invoiceType && part) {
        const fieldValues = await createFillFields({
          invoiceType: invoice.invoiceType,
          invoiceId: invoice?.id,
          partId: part?.partId,
        });

        form.setFieldsValue(fieldValues);
      }
    })();
  }, [invoice, part]);

  useEffect(() => {
    // Filling in fields when editing the note item
    (async () => {
      if (invoiceItemId) {
        const fields = await detailFillFields(invoiceItemId);
        form.setFieldsValue(fields);
      }
    })();
  }, [invoiceItemId]);

  const isOriginPurchase = useMemo(() => {
    return (
      !!invoice &&
      !!invoice?.originReference?.some(
        (item) => item.entity === InvoiceOriginReferenceEntity.PURCHASE,
      )
    );
  }, [invoice]);

  function handleClose() {
    form.resetFields();
    setMenuKey('general');
    setTabErrors([]);
    props.onCancel();
  }

  async function handleSubmit(closeModal = true) {
    try {
      setSubmitLoading(true);

      const values = await form.validateFields();

      const itemTaxes: Partial<InvoiceItemTaxes> = {
        ...values.itemTaxes.icms,
        ...values.itemTaxes.ipi,
        ...values.itemTaxes.pis,
        ...values.itemTaxes.cofins,
        ...values.itemTaxes.share,
        ...values.itemTaxes.irpjCsll,
        // IS (Imposto Seletivo, UB01-UB11) — fora da NT v1.40; reativar
        // quando entrar em vigor.
        // ...values.itemTaxes.is,
        ...values.itemTaxes.ibsCbs,
        competenceApuration: values.itemTaxes?.ibsCbs?.competenceApuration
          ? values.itemTaxes?.ibsCbs?.competenceApuration.format('YYYY-MM-DD')
          : undefined,
        ibsZfmAssessmentPeriod:
          values.itemTaxes.ibsCbs?.ibsZfmAssessmentPeriod?.format('YYYY-MM'),
      };

      const payload: InvoiceItemCreateRequest = {
        ...values.general,
        invoiceId: invoice?.id,
        type: TypeEnum.part,
        itemTaxes: itemTaxes as InvoiceItemTaxes,
        fuel: values.fuel,
        cest: removeMask(values.general.cest),
      };

      if (invoiceItemId) {
        await invoiceItemEdit.mutateAsync({
          ...payload,
          invoiceId: invoice?.id,
          id: invoiceItemId,
          hiddeNotification: !closeModal,
        });
      } else {
        await invoiceItemCreate.mutateAsync({
          ...payload,
          partId: part?.partId,
          consumableId: part?.consumableId,
        });
      }

      if (closeModal) {
        handleClose();
      }

      return true;
    } catch (err: unknown) {
      message.error('Preencha os campos obrigatórios');

      const error = err as { errorFields?: { name: string[] }[] };

      if (error.errorFields?.length) {
        setTabErrors(
          error.errorFields.reduce((acc, curr) => {
            const name =
              curr.name[0] === 'itemTaxes' ? curr.name[1] : curr.name[0];

            if (!acc.includes(name)) {
              return [...acc, name];
            }

            return acc;
          }, [] as string[]),
        );
      }

      return false;
    } finally {
      setSubmitLoading(false);
    }
  }

  const calculateTaxes = useCallback(async () => {
    try {
      if (invoiceItemId) {
        // Save fields filled
        const result = await handleSubmit(false);

        if (result) {
          // Calcule taxes
          await invoiceCalculateTaxesItem.mutateAsync(invoiceItemId);

          // Update taxes fields
          const fields = await detailFillFields(invoiceItemId);
          form.setFieldsValue(fields);
        }
      }
    } catch {}
  }, [invoiceItemId]);

  const modalTitle = (
    <Group gap={16}>
      <StatusTag label={invoice?.invoiceType} />
      <span>
        {invoiceItemId ? 'Editar peça da nota' : 'Vincular peça na nota'}
      </span>
    </Group>
  );

  const modalFooter = (
    <Group position="between" gap={24}>
      <div>
        {!!invoiceItemId && (
          <Button
            type="primary"
            disabled={hasLoading || disabledFields}
            onClick={calculateTaxes}
          >
            Calcular tributos
          </Button>
        )}
      </div>

      <Group gap={24}>
        <Button onClick={handleClose} disabled={hasLoading}>
          Cancelar
        </Button>
        <SaveButton
          onClick={() => handleSubmit()}
          disabled={hasLoading || disabledFields}
        >
          {invoiceItemId ? 'Alterar peça' : 'Vincular peça'}
        </SaveButton>
      </Group>
    </Group>
  );

  const tabIconError = (key: string) =>
    tabErrors.includes(key) ? <Badge dot /> : null;

  return (
    <Modal
      {...others}
      onCancel={handleClose}
      title={modalTitle}
      width={1200}
      bodyStyle={{ padding: 0 }}
      maskClosable={false}
      destroyOnClose
      footer={modalFooter}
      closable={!hasLoading}
    >
      <div style={{ marginBottom: 24, position: 'relative' }}>
        {(hasLoading || createFieldsLoading) && (
          <ModalLoadingOverlay
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backdropFilter: 'blur(1px)',
            }}
            messages={
              submitLoading
                ? [
                    invoiceItemId
                      ? 'Salvando dados do item...'
                      : 'Vinculando item na nota...',
                    'Atualizando listagem de itens da nota...',
                  ]
                : invoiceCalculateTaxesItem.isLoading
                ? ['Calculando dados de tributos...']
                : ['Preenchendo dados da peça e tributação...']
            }
          />
        )}

        <TabsCustom key="general" activeKey={menuKey} onChange={setMenuKey}>
          <TabsCustom.Item
            label="Dados gerais"
            key="general"
            icon={tabIconError('general')}
          >
            <InvoicePartGeneralDataForm
              form={form}
              isOriginPurchase={isOriginPurchase}
              partHasBarCode={!!part?.barcode}
              invoiceItemId={invoiceItemId}
              companyId={invoice?.companyId}
              invoiceType={invoice?.invoiceType}
              key={Number(createFieldsLoading || detailFieldsLoading)}
            />
          </TabsCustom.Item>

          <TabsCustom.Item key="icms" label="ICMS" icon={tabIconError('icms')}>
            <InvoicePartIcmsForm form={form} invoiceItemId={invoiceItemId} />
          </TabsCustom.Item>

          <TabsCustom.Item
            key="share"
            label="Partilha"
            icon={tabIconError('share')}
          >
            <InvoicePartShareForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item key="ipi" label="IPI" icon={tabIconError('ipi')}>
            <InvoicePartIpiForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item key="pis" label="PIS" icon={tabIconError('pis')}>
            <InvoicePartPisForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item
            key="cofins"
            label="COFINS"
            icon={tabIconError('cofins')}
          >
            <InvoicePartCofinsForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item
            key="irpjCsll"
            label="IRRF/CSLL"
            icon={tabIconError('irpjCsll')}
          >
            <InvoicePartIrpjCsllForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item
            key="fuel"
            label="Combustível"
            icon={tabIconError('fuel')}
          >
            <InvoicePartFuelForm form={form} />
          </TabsCustom.Item>

          {/* IS (Imposto Seletivo, UB01-UB11) — fora da NT v1.40; reativar
              quando entrar em vigor. */}
          {/* <TabsCustom.Item key="is" label="IS" icon={tabIconError('is')}>
            <IsFormFields form={form} disabledFields={disabledFields} />
          </TabsCustom.Item> */}

          <TabsCustom.Item
            key="ibsCbs"
            label="IBS/CBS"
            icon={tabIconError('ibsCbs')}
          >
            <IbsCbsFormFields
              form={form}
              disabledFields={disabledFields}
              invoiceType={invoice?.invoiceType}
            />
          </TabsCustom.Item>
        </TabsCustom>
      </div>
    </Modal>
  );
};

export default InvoicePartModal;
